/**
 * NotificationSettings - Modal for SMS notification preferences.
 * Users can toggle SMS notifications on/off and set their phone number.
 */

import { useState, useEffect } from 'react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

interface NotificationSettingsProps {
  isOpen: boolean
  onClose: () => void
}

export function NotificationSettings({ isOpen, onClose }: NotificationSettingsProps) {
  const [smsEnabled, setSmsEnabled] = useState(false)
  const [phone, setPhone] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  // Load current preferences
  useEffect(() => {
    if (!isOpen) return
    const token = localStorage.getItem('auth_token')
    if (!token) return

    setLoading(true)
    fetch(`${API_URL}/api/groupchat/notifications`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setSmsEnabled(d.smsEnabled)
          setPhone(d.smsPhone || '')
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [isOpen])

  const handleSave = async () => {
    setSaving(true)
    setError('')
    setSaved(false)

    if (smsEnabled && !phone.trim()) {
      setError('Please enter a phone number to receive SMS notifications')
      setSaving(false)
      return
    }

    try {
      const token = localStorage.getItem('auth_token')
      const res = await fetch(`${API_URL}/api/groupchat/notifications`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          smsEnabled,
          smsPhone: phone.trim() || null
        })
      })

      if (!res.ok) {
        const d = await res.json()
        setError(d.error || 'Failed to save')
        setSaving(false)
        return
      }

      setSaved(true)
      setTimeout(() => onClose(), 1200)
    } catch {
      setError('Failed to save preferences')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 border border-pink-200"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-gray-900 mb-1">Notifications</h2>
        <p className="text-sm text-gray-500 mb-5">Get notified about new messages in group chat.</p>

        {loading ? (
          <div className="text-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pink-500 mx-auto" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* SMS Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">SMS Notifications</p>
                <p className="text-xs text-gray-400">Receive text messages for important updates</p>
              </div>
              <button
                onClick={() => setSmsEnabled(!smsEnabled)}
                className={`relative w-11 h-6 rounded-full transition-colors ${smsEnabled ? 'bg-pink-500' : 'bg-gray-300'}`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${smsEnabled ? 'translate-x-5' : ''}`}
                />
              </button>
            </div>

            {/* Phone number (only when SMS enabled) */}
            {smsEnabled && (
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="w-full bg-pink-50 border border-pink-200 rounded-xl px-4 py-2.5 text-base text-gray-800 placeholder-gray-400 focus:outline-none focus:border-pink-400"
                />
                <p className="text-[10px] text-gray-400 mt-1">Standard messaging rates may apply</p>
              </div>
            )}

            {error && <p className="text-sm text-red-500 text-center">{error}</p>}
            {saved && <p className="text-sm text-green-600 text-center">Saved!</p>}

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl text-gray-500 text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-40"
                style={{ backgroundColor: '#ec4899' }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
